import { Form } from "react-bootstrap";
import messages from "messages";
import moment from "moment";
import { forwardRef, useMemo, useRef, useImperativeHandle } from "react";
// components
import DatePicker from "components/date-picker/DatePicker";
import RequiredAsterisk from "components/required-asterisk";
import Checkbox from "components/checkbox/Checkbox";
// utils
import offsetInterval from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/utils/offsetInterval";
// styles
import styles from "./styles.module.scss";

const DATE_FORMAT = "MM/dd/yyyy";

const INPUT_IDS = {
  startDate: "startDate",
  endDate: "endDate",
};

type Props = {
  timezone: TimeZone;
  value: ParsedAvailableTimePeriod;
  onChange: (arg: ParsedAvailableTimePeriod) => void;
};

/**
 * renders UI for modifying schedule date range
 */
const ForwardedScheduleDateRange = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function ScheduleDateRange(
  { timezone, value: { start_date, end_date, intervals, recurring }, onChange },
  ref,
) {
  const weekStartDateValue = useMemo(
    () =>
      moment
        .tz(start_date || Date.now(), timezone)
        .startOf("week")
        .valueOf(),
    [start_date, timezone],
  );

  const startDate = useMemo(
    () => (!start_date ? null : moment.tz(start_date, timezone).startOf("day")),
    [start_date, timezone],
  );

  const endDate = useMemo(
    () => (!end_date ? null : moment.tz(end_date, timezone).endOf("day")),
    [end_date, timezone],
  );

  const minDate = useMemo(
    () => startDate || moment().tz(timezone).startOf("day"),
    [startDate, timezone],
  );

  const inputRefs = useRef<
    Record<
      "start" | "end",
      Pick<HTMLFormElement, "checkValidity" | "reset"> | null
    >
  >({ start: null, end: null });

  useImperativeHandle(ref, () => ({
    checkValidity: () =>
      [inputRefs.current.start, inputRefs.current.end].every((el) => {
        if (!el) {
          return true;
        }
        return el.checkValidity();
      }),
    reset: () =>
      [inputRefs.current.start, inputRefs.current.end].every((el) => {
        if (!el) {
          return true;
        }
        return el.reset();
      }),
  }));

  return (
    <Form.Group controlId="dateRange" className={styles["schedule-date-range"]}>
      <Form.Label className={styles["schedule-date-range__group-label"]}>
        {messages.dateRange}
      </Form.Label>
      <div className={styles["schedule-date-range__group"]}>
        <div className={styles["schedule-date-range__item"]}>
          <label
            className={styles["schedule-date-range__input-label"]}
            htmlFor={INPUT_IDS.startDate}
          >
            {messages.startDate}
            <RequiredAsterisk />
          </label>
          <DatePicker
            ref={(el) => {
              inputRefs.current.start = el;
            }}
            id={INPUT_IDS.startDate}
            returnStartOfDay={true}
            timezone={timezone}
            selected={startDate?.toDate()}
            onChange={(date: Date) => {
              // compare week start
              const days = moment(+date)
                .tz(timezone)
                .startOf("week")
                .diff(moment(weekStartDateValue).tz(timezone), "day");
              // migrate old schedule to the new week start
              if (!endDate) {
                const nextIntervals = intervals.map((el) =>
                  el === null
                    ? null
                    : el.map((each) => offsetInterval(each, days, timezone)),
                );
                return onChange({
                  recurring,
                  end_date,
                  intervals: nextIntervals,
                  start_date: (date as Date).toISOString(),
                });
              }

              const momentStartDate = moment(date).tz(timezone);
              const nextDayDiff = !date
                ? 7
                : Math.min(
                    7,
                    Math.max(0, endDate.diff(momentStartDate, "day") + 1),
                  );

              const weekDayIds = Array(nextDayDiff)
                .fill(0)
                .map((_, i) => momentStartDate.weekday() + i);

              const nextIntervals = intervals.map((el, i) =>
                weekDayIds.includes(i) && el
                  ? el.map((each) => offsetInterval(each, days, timezone))
                  : null,
              );

              return onChange({
                recurring,
                end_date,
                intervals: nextIntervals,
                start_date: (date as Date).toISOString(),
              });
            }}
            maxDate={endDate?.toDate()}
            placeholderText={messages.selectStartDate}
            required={true}
            wrapperClassName={styles["schedule-date-range__input"]}
            validityMessage={messages.selectAStartDate}
            dateFormat={DATE_FORMAT}
          />
        </div>

        <div className={styles["schedule-date-range__item"]}>
          <label
            className={styles["schedule-date-range__input-label"]}
            htmlFor={INPUT_IDS.endDate}
          >
            {messages.endDate}
            {!recurring && <RequiredAsterisk />}
          </label>

          <DatePicker
            ref={(el) => {
              inputRefs.current.end = el;
            }}
            id={INPUT_IDS.endDate}
            timezone={timezone}
            selected={endDate?.toDate()}
            onChange={(date: Date) => {
              const nextDayDiff = !date
                ? 7
                : Math.min(
                    7,
                    Math.max(
                      0,
                      moment(date).tz(timezone).diff(startDate, "day") + 1,
                    ),
                  );
              const weekDayIds = Array(nextDayDiff)
                .fill(0)
                .map((_, i) => ((startDate || minDate).weekday() + i) % 7);

              const nextIntervals = intervals.map((el, i) =>
                weekDayIds.includes(i) ? el : null,
              );

              return onChange({
                recurring,
                start_date,
                intervals: nextIntervals,
                end_date: (date as Date).toISOString(),
              });
            }}
            minDate={minDate.toDate()}
            placeholderText={messages.selectEndDate}
            disabled={recurring}
            required={!recurring}
            validityMessage={messages.selectAnEndDate}
            wrapperClassName={styles["schedule-date-range__input"]}
            dateFormat={DATE_FORMAT}
          />
        </div>
        <div
          className={`${styles["schedule-date-range__item"]} ${styles["schedule-date-range__item--recurring"]}`}
        >
          <Checkbox
            variant="rectangle"
            labelClassName={styles["schedule-date-range__recurring-label"]}
            label={messages.noEndDate}
            checked={recurring}
            onChange={(e) => {
              onChange({
                start_date,
                intervals,
                end_date: e.target.checked ? null : end_date,
                recurring: e.target.checked,
              });
              inputRefs.current.end?.reset();
            }}
          />
        </div>
      </div>
    </Form.Group>
  );
});

export default ForwardedScheduleDateRange;
